'use client';

/**
 * SetupChecklistWidget — refined onboarding checklist pentru dashboard.
 *
 * Design language: Linear/Vercel-quality, refined editorial.
 * - Header inline cu progress bar slim
 * - Group breadcrumb (chip row) — info densă
 * - Pending în 2-col grid (focus)
 * - Done compactat în pill row (de-emphasized)
 * - Opțional collapsed by default
 * - 100% reward banner separat (păstrat thematic — Sparkles)
 *
 * Auto-detection 100% (fără bifare manuală).
 */

import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Check,
  Circle,
  X,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Loader2,
  ListChecks,
  EyeOff,
} from 'lucide-react';
import {
  useSetupStatus,
  useDismissChecklist,
  useMarkCompletionSeen,
  useMarkItemNotApplicable,
} from '@/features/onboarding/hooks/use-setup-status';
import type {
  ChecklistItem,
  ChecklistGroup,
} from '@/features/onboarding/services/setup-status-api';

const GROUP_ORDER: ChecklistGroup[] = ['essential', 'master', 'activation', 'optional'];

export function SetupChecklistWidget() {
  const { data, isLoading } = useSetupStatus();
  const dismiss = useDismissChecklist();
  const markSeen = useMarkCompletionSeen();
  const markNotApplicable = useMarkItemNotApplicable();
  const router = useRouter();
  const [confirmItem, setConfirmItem] = useState<{ id: string; label: string } | null>(null);

  if (isLoading || !data) return null;
  if (data.dismissedAt) return null;
  if (data.allComplete && data.completedAt) return null;

  // ── REWARD BANNER (100% never seen) ──
  if (data.allComplete && !data.completedAt) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-emerald-200 dark:border-emerald-800/60 bg-white dark:bg-neutral-950">
        {/* subtle gradient overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-50/70 via-transparent to-transparent dark:from-emerald-950/30"
        />
        <div className="relative flex items-center gap-4 px-5 py-4">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100 dark:bg-emerald-900/40 ring-1 ring-emerald-200/60 dark:ring-emerald-800/60">
            <Sparkles className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Felicitări — contul tău este complet configurat
            </div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Toate cele {data.totalItems} setări sunt în regulă. Succes cu facturarea!
            </div>
          </div>
          <button
            onClick={() => markSeen.mutate()}
            disabled={markSeen.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-50"
          >
            {markSeen.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            Mulțumesc
          </button>
        </div>
      </div>
    );
  }

  // ── Partition items ──
  const nonOptional = data.items.filter((i) => i.group !== 'optional');
  const optionalItems = data.items.filter((i) => i.group === 'optional');
  const pendingItems = nonOptional.filter((i) => !i.done);
  const doneItems = nonOptional.filter((i) => i.done);

  return (
    <>
    <div className="relative overflow-hidden rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      {/* ─────── HEADER ─────── */}
      <div className="relative">
        {/* very subtle gradient backing for header only */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-br from-primary-50/60 via-transparent to-transparent dark:from-primary-900/20"
        />
        <div className="relative flex items-center gap-3 px-5 pt-4 pb-3">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-primary-200/70 dark:ring-primary-800/60 shadow-sm shrink-0">
            <ListChecks className="h-4.5 w-4.5 text-primary-600 dark:text-primary-400" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <h3 className="text-[0.9375rem] font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                Configurarea contului
              </h3>
              <span className="text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">
                · {data.completedCount}/{data.totalItems}
              </span>
            </div>
          </div>

          <div className="flex items-baseline gap-1 shrink-0">
            <span className="text-2xl font-semibold tabular-nums text-neutral-900 dark:text-neutral-100 leading-none">
              {data.percentComplete}
            </span>
            <span className="text-xs font-medium text-neutral-400 dark:text-neutral-500">%</span>
          </div>

          <button
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending}
            aria-label="Închide checklist"
            // „butonul plutitor" nu mai există — a fost înlocuit de trigger-ul
            // din top-bar (setup-checklist-topbar-trigger.tsx, indicatorul %).
            title="Îl poți redeschide oricând din indicatorul de progres (%) din bara de sus"
            className="ml-1 grid h-7 w-7 place-items-center rounded-md text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* slim progress bar */}
        <div className="relative h-[3px] mx-5 mb-3 rounded-full bg-neutral-100 dark:bg-neutral-800/80 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 via-primary-500 to-emerald-500 transition-[width] duration-500 ease-out rounded-full"
            style={{ width: `${data.percentComplete}%` }}
          />
        </div>
      </div>

      {/* ─────── GROUP CHIPS (breadcrumb-style) ─────── */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 px-5 pb-3 border-b border-neutral-100 dark:border-neutral-800/80">
        {GROUP_ORDER.map((g, idx) => {
          const stat = data.groups[g];
          if (!stat || stat.total === 0) return null;
          const complete = stat.completed === stat.total;
          return (
            <div key={g} className="flex items-center gap-1.5">
              {idx > 0 && (
                <span className="text-neutral-300 dark:text-neutral-700 text-xs select-none">·</span>
              )}
              <span
                className={`text-[0.6875rem] font-medium tracking-wide ${
                  complete
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-neutral-600 dark:text-neutral-400'
                }`}
              >
                {stat.label}
              </span>
              <span
                className={`text-[0.6875rem] font-semibold tabular-nums px-1.5 py-0.5 rounded ${
                  complete
                    ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400'
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'
                }`}
              >
                {stat.completed}/{stat.total}
              </span>
            </div>
          );
        })}
      </div>

      {/* ─────── PENDING ITEMS (2-col grid) ─────── */}
      {pendingItems.length > 0 && (
        <div className="px-5 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pendingItems.map((item) => (
              <PendingCard
                key={item.id}
                item={item}
                onClick={() => router.push(item.cta.href)}
                onMarkNotApplicable={() => setConfirmItem({ id: item.id, label: item.label })}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─────── DONE ITEMS (compact pill row) ─────── */}
      {doneItems.length > 0 && (
        <div className="px-5 pb-3 -mt-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-[0.6563rem] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mr-1">
              <Check className="h-3 w-3" strokeWidth={3} />
              {doneItems.length} completate
            </span>
            {doneItems.map((item) => (
              <span
                key={item.id}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[0.6563rem] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50/70 dark:bg-emerald-950/40 ring-1 ring-inset ring-emerald-100 dark:ring-emerald-900/40"
                title={item.description}
              >
                {item.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ─────── OPTIONAL (collapsed) ─────── */}
      {optionalItems.length > 0 && (
        <details className="group border-t border-neutral-100 dark:border-neutral-800/80">
          <summary className="flex items-center justify-between px-5 py-2.5 cursor-pointer list-none select-none hover:bg-neutral-50 dark:hover:bg-neutral-900/40">
            <div className="flex items-center gap-2">
              <ChevronDown className="h-3.5 w-3.5 text-neutral-400 transition-transform group-open:rotate-180" />
              <span className="text-[0.6875rem] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                {data.groups.optional.label}
              </span>
              <span className="text-[0.6563rem] font-semibold tabular-nums text-neutral-400 dark:text-neutral-500">
                ({data.groups.optional.completed}/{data.groups.optional.total})
              </span>
            </div>
          </summary>
          <div className="px-5 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {optionalItems.map((item) =>
                item.done ? (
                  <DoneCard
                    key={item.id}
                    item={item}
                    onMarkNotApplicable={() => setConfirmItem({ id: item.id, label: item.label })}
                  />
                ) : (
                  <PendingCard
                    key={item.id}
                    item={item}
                    onClick={() => router.push(item.cta.href)}
                    onMarkNotApplicable={() => setConfirmItem({ id: item.id, label: item.label })}
                  />
                )
              )}
            </div>
          </div>
        </details>
      )}
    </div>
      {/* Confirmation modal: marchează ca neaplicabil */}
      {confirmItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmItem(null)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white dark:bg-neutral-900 shadow-xl ring-1 ring-neutral-200 dark:ring-neutral-700 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 shrink-0">
                <EyeOff className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Elimini acest pas din listă?</h3>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                  &bdquo;{confirmItem.label}&rdquo; nu va mai apărea în lista de configurare și nu va conta la progres.
                  Dacă te răzgândești, îl poți readuce oricând din <strong>Setări → Excluse din configurare</strong>.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConfirmItem(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              >
                Anulează
              </button>
              <button
                type="button"
                disabled={markNotApplicable.isPending}
                onClick={async () => {
                  const id = confirmItem.id;
                  const label = confirmItem.label;
                  setConfirmItem(null);
                  try {
                    await markNotApplicable.mutateAsync(id);
                    toast.success('Pas eliminat din listă', {
                      description: '„' + label + '" — îl poți readuce din Setări → Excluse din configurare',
                    });
                  } catch {
                    toast.error('Nu s-a putut marca itemul. Încearcă din nou.');
                  }
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-700 px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
              >
                {markNotApplicable.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Elimină din listă
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function PendingCard({
  item,
  onClick,
  onMarkNotApplicable,
}: {
  item: ChecklistItem;
  onClick: () => void;
  onMarkNotApplicable?: () => void;
}) {
  return (
    <div className="group relative text-left flex items-center gap-3 px-3 py-2.5 rounded-lg bg-primary-50/70 dark:bg-primary-900/25 ring-1 ring-inset ring-primary-200 dark:ring-primary-800/60 hover:ring-primary-500 dark:hover:ring-primary-500/80 hover:bg-primary-100/60 dark:hover:bg-primary-900/40 hover:shadow-sm hover:shadow-primary-900/5 transition-all">
      <button
        type="button"
        onClick={onClick}
        className="absolute inset-0 rounded-lg z-0"
        aria-label={`Deschide ${item.label}`}
      />
      <div className="relative z-10 grid h-4 w-4 place-items-center rounded-full bg-primary-500/15 ring-1 ring-primary-400/50 dark:ring-primary-500/40 shrink-0 group-hover:bg-primary-500/25 transition-colors pointer-events-none">
        <div className="h-1.5 w-1.5 rounded-full bg-primary-500 dark:bg-primary-400" />
      </div>
      <div className="relative z-10 flex-1 min-w-0 pointer-events-none">
        <div className="text-[0.8125rem] font-semibold text-neutral-900 dark:text-neutral-100 truncate">
          {item.label}
        </div>
        {item.description && (
          <div className="text-[0.6875rem] text-neutral-600 dark:text-neutral-400 truncate mt-0.5">
            {item.description}
          </div>
        )}
      </div>
      <div className="relative z-10 inline-flex items-center gap-1 shrink-0 px-2.5 py-1 rounded-md bg-primary-600 dark:bg-primary-600 text-white text-[0.6875rem] font-semibold shadow-sm shadow-primary-900/15 group-hover:bg-primary-700 dark:group-hover:bg-primary-500 group-hover:shadow group-hover:translate-x-0.5 transition-all whitespace-nowrap pointer-events-none">
        {item.cta.label}
        <ChevronRight className="h-3 w-3" />
      </div>
      {onMarkNotApplicable && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMarkNotApplicable(); }}
          className="relative z-20 grid h-6 w-6 shrink-0 place-items-center rounded text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
          aria-label={`Elimină „${item.label}" din listă`}
          title="Nu mi se aplică — elimină din listă"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DoneCard — variantă pentru itemii optionali completați. Aceeași structură
// vizuală (verde + bifă + label) ca înainte, dar cu buton „✕" în dreapta care
// deschide dialogul de eliminare (item dispare din checklist).
// ─────────────────────────────────────────────────────────────────────────────
function DoneCard({
  item,
  onMarkNotApplicable,
}: {
  item: ChecklistItem;
  onMarkNotApplicable: () => void;
}) {
  return (
    <div className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/40 dark:bg-emerald-950/20 ring-1 ring-inset ring-emerald-100 dark:ring-emerald-900/40">
      <div className="grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-white shrink-0">
        <Check className="h-2.5 w-2.5" strokeWidth={3} />
      </div>
      <span className="text-[0.7813rem] font-medium text-emerald-800 dark:text-emerald-300 truncate flex-1">
        {item.label}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onMarkNotApplicable(); }}
        className="grid h-6 w-6 shrink-0 place-items-center rounded text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
        aria-label={`Elimină „${item.label}" din listă`}
        title="Nu mi se aplică — elimină din listă"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
